import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  MapPin,
  Phone,
  MessageCircle,
  Mail,
  AtSign,
  Clock,
  Linkedin,
  Map,
  CheckCircle2,
} from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import Honeypot from "@/components/Honeypot";
import { siteConfig, whatsappHref, isPlaceholder } from "@/config/siteConfig";
import { submitLead, readAttribution, baseMeta } from "@/lib/api";
import { trackLeadSubmit } from "@/lib/analytics";
import TurnstileWidget, { useSubmitBlockedByTurnstile } from "@/components/TurnstileWidget";

// Business hours are the standard UAE business week. Safe to hardcode for now;
// can be moved to siteConfig later if we start operating on non-standard hours.
const BUSINESS_HOURS = "Sunday – Thursday, 09:00 – 18:00 GST";

const ALL_CONTACT_ITEMS = [
  { Icon: MapPin, label: "Head Office", value: siteConfig.ADDRESS_STREET, key: "address" },
  { Icon: Phone, label: "Phone", value: siteConfig.PHONE, key: "phone" },
  { Icon: MessageCircle, label: "WhatsApp", value: siteConfig.WHATSAPP, key: "whatsapp", href: whatsappHref() },
  { Icon: Mail, label: "Sales Email", value: siteConfig.EMAIL_SALES, key: "email-sales", href: `mailto:${siteConfig.EMAIL_SALES}` },
  { Icon: AtSign, label: "Technical Email", value: siteConfig.EMAIL_TECH, key: "email-tech", href: `mailto:${siteConfig.EMAIL_TECH}` },
  { Icon: Clock, label: "Business Hours", value: BUSINESS_HOURS, key: "hours" },
  { Icon: Linkedin, label: "LinkedIn", value: siteConfig.LINKEDIN_URL, key: "linkedin", href: siteConfig.LINKEDIN_URL },
];

// Filter to only items with real values — placeholder-valued cards are hidden.
const CONTACT_ITEMS = ALL_CONTACT_ITEMS.filter((it) => !isPlaceholder(it.value));

const TABS = [
  { key: "general", label: "General Enquiry" },
  { key: "technical", label: "Technical Enquiry" },
  { key: "sample", label: "Request Sample" },
  { key: "distributor", label: "Distributor Enquiry" },
];

const Field = ({ label, name, type = "text", value, onChange, required }) => {
  const id = `c-${name}`;
  const common =
    "w-full border border-[var(--border)] rounded-[4px] px-3 py-2.5 text-[14px] text-[var(--charcoal)] bg-white focus:border-[var(--pharma-blue)] focus:outline-none focus:ring-1 focus:ring-[var(--pharma-blue)]";
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[13px] font-medium text-[var(--charcoal)] mb-1.5"
      >
        {label} {required && <span className="text-[var(--pharma-blue)]">*</span>}
      </label>
      {type === "textarea" ? (
        <textarea
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          rows={4}
          className={common}
          data-testid={`c-${name}`}
        />
      ) : (
        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          required={required}
          className={common}
          data-testid={`c-${name}`}
          autoComplete="off"
        />
      )}
    </div>
  );
};

const SuccessCard = ({ testid }) => (
  <div
    className="border border-[var(--border)] rounded-[4px] p-8 bg-[var(--bg-alt)]"
    data-testid={testid}
    role="status"
  >
    <CheckCircle2
      strokeWidth={1.5}
      style={{ width: 40, height: 40, color: "var(--pharma-blue)" }}
      aria-hidden="true"
    />
    <p className="font-heading mt-4 text-[19px] font-semibold text-[var(--navy)]">
      Thank you.
    </p>
    <p className="mt-3 text-[14.5px] text-[var(--charcoal)]">
      A member of our team will review your enquiry and follow up shortly.
    </p>
  </div>
);

const GeneralForm = ({ prefilledProduct, attribution }) => {
  const [form, setForm] = useState({
    contactName: "",
    company: "",
    email: "",
    country: "",
    message: "",
  });
  const [hp, setHp] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaBlocks = useSubmitBlockedByTurnstile(captchaToken);
  const inflightRef = useRef(false);
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    if (inflightRef.current) return;
    inflightRef.current = true;
    setSubmitting(true);
    setError(null);
    const res = await submitLead({
      type: "GENERAL_ENQUIRY",
      source_page: "/contact",
      contact: {
        name: form.contactName,
        company: form.company || null,
        email: form.email,
        country: form.country || "unspecified",
      },
      attribution: { ...attribution, ...(prefilledProduct ? { product_slug: prefilledProduct } : {}) },
      meta: baseMeta(),
      payload: { message: form.message },
      _hp: hp,
      turnstile_token: captchaToken,
    });
    setSubmitting(false);
    inflightRef.current = false;
    if (res.ok) {
      trackLeadSubmit("general");
      setSubmitted(true);
    }
    else if (res.status === 403) setError("Verification failed. Please retry the challenge.");
    else if (res.status === 429) setError("You are submitting too frequently. Please try again in a minute.");
    else setError("Please check the required fields and try again.");
  };
  if (submitted) return <SuccessCard testid="c-success-general" />;
  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="c-form-general" noValidate>
      {error && (
        <div className="sm:col-span-2 border border-[var(--pharma-blue)] rounded-[4px] p-3 bg-[var(--bg-alt)] text-[13.5px] text-[var(--charcoal)]" data-testid="c-error-general">
          {error}
        </div>
      )}
      <Field label="Name" name="contactName" required value={form.contactName} onChange={onChange} />
      <Field label="Company" name="company" value={form.company} onChange={onChange} />
      <Field label="Business Email" name="email" type="email" required value={form.email} onChange={onChange} />
      <Field label="Country" name="country" value={form.country} onChange={onChange} />
      <div className="sm:col-span-2">
        <Field label="Message" name="message" type="textarea" required value={form.message} onChange={onChange} />
      </div>
      <Honeypot value={hp} onChange={(e) => setHp(e.target.value)} />
      <div className="sm:col-span-2">
        <TurnstileWidget onToken={setCaptchaToken} action="contact_general" testid="c-turnstile-general" />
        <button type="submit" disabled={submitting || captchaBlocks} className="btn-primary text-sm disabled:opacity-70" data-testid="c-submit-general">
          {submitting ? "Submitting…" : "Submit Enquiry"}
        </button>
      </div>
    </form>
  );
};

const TechnicalForm = ({ prefilledProduct, attribution }) => {
  const [form, setForm] = useState({
    contactName: "",
    company: "",
    email: "",
    country: "",
    product: prefilledProduct || "",
    machineBrand: "",
    substrate: "",
    issue: "",
  });
  useEffect(() => {
    if (prefilledProduct) setForm((f) => ({ ...f, product: prefilledProduct }));
  }, [prefilledProduct]);
  const [hp, setHp] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaBlocks = useSubmitBlockedByTurnstile(captchaToken);
  const inflightRef = useRef(false);
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    if (inflightRef.current) return;
    inflightRef.current = true;
    setSubmitting(true);
    setError(null);
    const res = await submitLead({
      type: "TECHNICAL_ENQUIRY",
      source_page: "/contact",
      contact: {
        name: form.contactName,
        company: form.company,
        email: form.email,
        country: form.country,
      },
      attribution: { ...attribution, ...(form.product ? { product_slug: form.product } : {}) },
      meta: baseMeta(),
      payload: {
        product_slug: form.product || null,
        machine_brand: form.machineBrand || null,
        substrate: form.substrate || null,
        issue_or_requirement: form.issue,
      },
      _hp: hp,
      turnstile_token: captchaToken,
    });
    setSubmitting(false);
    inflightRef.current = false;
    if (res.ok) {
      trackLeadSubmit("technical");
      setSubmitted(true);
    }
    else if (res.status === 403) setError("Verification failed. Please retry the challenge.");
    else if (res.status === 429) setError("You are submitting too frequently. Please try again in a minute.");
    else setError("Please check the required fields and try again.");
  };
  if (submitted) return <SuccessCard testid="c-success-technical" />;
  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="c-form-technical" noValidate>
      {error && (
        <div className="sm:col-span-2 border border-[var(--pharma-blue)] rounded-[4px] p-3 bg-[var(--bg-alt)] text-[13.5px] text-[var(--charcoal)]" data-testid="c-error-technical">
          {error}
        </div>
      )}
      <Field label="Name" name="contactName" required value={form.contactName} onChange={onChange} />
      <Field label="Company" name="company" required value={form.company} onChange={onChange} />
      <Field label="Business Email" name="email" type="email" required value={form.email} onChange={onChange} />
      <Field label="Country" name="country" required value={form.country} onChange={onChange} />
      <Field label="Product of Interest" name="product" value={form.product} onChange={onChange} />
      <Field label="Machine Brand" name="machineBrand" value={form.machineBrand} onChange={onChange} />
      <div className="sm:col-span-2">
        <Field label="Substrate" name="substrate" value={form.substrate} onChange={onChange} />
      </div>
      <div className="sm:col-span-2">
        <Field label="Issue or Requirement" name="issue" type="textarea" required value={form.issue} onChange={onChange} />
      </div>
      <Honeypot value={hp} onChange={(e) => setHp(e.target.value)} />
      <div className="sm:col-span-2">
        <TurnstileWidget onToken={setCaptchaToken} action="contact_technical" testid="c-turnstile-technical" />
        <button type="submit" disabled={submitting || captchaBlocks} className="btn-primary text-sm disabled:opacity-70" data-testid="c-submit-technical">
          {submitting ? "Submitting…" : "Submit Technical Enquiry"}
        </button>
      </div>
    </form>
  );
};

const RedirectPanel = ({ heading, body, ctaLabel, ctaTo, testid }) => (
  <div className="border border-[var(--border)] rounded-[4px] p-8 bg-[var(--bg-alt)]" data-testid={testid}>
    <h3 className="font-heading text-[19px] font-semibold text-[var(--navy)]">
      {heading}
    </h3>
    <p className="mt-3 text-[14.5px] text-[var(--charcoal)]">{body}</p>
    <Link to={ctaTo} className="btn-primary text-sm mt-6" data-testid={`${testid}-cta`}>
      {ctaLabel}
    </Link>
  </div>
);

const Contact = () => {
  const path = "/contact";
  const canonical = `${siteConfig.DOMAIN}${path}`;
  const location = useLocation();
  const [params] = useSearchParams();

  const initialTab =
    ["general", "technical", "sample", "distributor"].includes(params.get("type"))
      ? params.get("type")
      : "general";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    if (["general", "technical", "sample", "distributor"].includes(params.get("type"))) {
      setTab(params.get("type"));
    }
  }, [params]);

  const prefilledProduct = params.get("product") || "";
  const attribution = readAttribution(params);
  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: `Contact ${siteConfig.COMPANY_NAME}`,
    url: canonical,
  };

  return (
    <>
      <SEO
        rawTitle={`Contact ${siteConfig.COMPANY_NAME} — Pharmaceutical Printing & Coding`}
        description={`Talk to our international team in Dubai about a pharmaceutical printing or coding requirement, sample request or distribution partnership.`}
        path={path}
        extraSchema={schema}
      />
      <Helmet>
      </Helmet>

      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Contact" }]} />
      </div>

      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10 pb-8"
        data-testid="contact-hero"
      >
        <p className="eyebrow">Contact</p>
        <h1
          className="font-heading mt-4 text-[32px] leading-tight lg:text-[46px] lg:leading-[1.05] font-semibold text-[var(--navy)]"
          data-testid="contact-h1"
        >
          Talk to Our International Team
        </h1>
        <p className="mt-6 text-[16px] text-[var(--charcoal)] max-w-3xl">
          Send us the details of your pharmaceutical printing or coding
          requirement — a member of our team will review your enquiry and follow
          up with a technical or commercial response.
        </p>
      </section>

      {/* Contact detail grid */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 py-8"
        data-testid="contact-details"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CONTACT_ITEMS.map(({ Icon, label, value, key, href }) => (
            <div
              key={key}
              className="border border-[var(--border)] rounded-[4px] p-5 bg-white flex items-start gap-4"
              data-testid={`contact-item-${key}`}
            >
              <Icon
                strokeWidth={1.5}
                style={{ width: 22, height: 22, color: "var(--pharma-blue)", flexShrink: 0 }}
                className="mt-1"
                aria-hidden="true"
              />
              <div>
                <p className="text-[12px] uppercase tracking-[0.12em] font-semibold text-[var(--slate)]">
                  {label}
                </p>
                {href ? (
                  <a
                    href={href}
                    className="mt-1.5 block text-[14.5px] text-[var(--charcoal)] hover:text-[var(--navy)] break-words"
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                  >
                    {value}
                  </a>
                ) : (
                  <p className="mt-1.5 text-[14.5px] text-[var(--charcoal)] break-words">
                    {value}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Maps embed — rendered only when a real embed URL/HTML is provided.
          Keeps the template in place so the block reappears automatically
          once siteConfig.MAPS_EMBED is populated. */}
      {!isPlaceholder(siteConfig.MAPS_EMBED) && (
        <section
          className="mx-auto max-w-[1240px] px-6 lg:px-10 py-6"
          data-testid="contact-map-placeholder"
        >
          <div className="border border-[var(--border)] rounded-[4px] bg-[var(--bg-alt)] p-10 flex items-center gap-4 justify-center text-center">
            <Map
              strokeWidth={1.5}
              style={{ width: 28, height: 28, color: "var(--slate)" }}
              aria-hidden="true"
            />
            <p className="text-[14px] text-[var(--slate)]">
              {siteConfig.MAPS_EMBED}
            </p>
          </div>
        </section>
      )}

      {/* Tabs */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 py-14 lg:py-20 border-t border-[var(--border)] mt-6"
        data-testid="contact-tabs-section"
      >
        <p className="eyebrow">Send an enquiry</p>
        <h2 className="font-heading mt-4 text-2xl lg:text-3xl font-semibold text-[var(--navy)]">
          Choose the type of enquiry
        </h2>

        <div
          role="tablist"
          aria-label="Contact enquiry type"
          className="mt-8 border-b border-[var(--border)] flex flex-wrap gap-x-6"
          data-testid="contact-tablist"
        >
          {TABS.map((tb) => {
            const active = tb.key === tab;
            return (
              <button
                key={tb.key}
                role="tab"
                type="button"
                aria-selected={active}
                onClick={() => setTab(tb.key)}
                className={`py-3 -mb-px text-[13px] uppercase tracking-[0.08em] font-semibold whitespace-nowrap transition-colors ${
                  active
                    ? "text-[var(--navy)] border-b-2 border-[var(--pharma-blue)]"
                    : "text-[var(--slate)] hover:text-[var(--navy)] border-b-2 border-transparent"
                }`}
                data-testid={`contact-tab-${tb.key}`}
              >
                {tb.label}
              </button>
            );
          })}
        </div>

        <div className="mt-8" key={location.search}>
          {tab === "general" && <GeneralForm prefilledProduct={prefilledProduct} attribution={attribution} />}
          {tab === "technical" && <TechnicalForm prefilledProduct={prefilledProduct} attribution={attribution} />}
          {tab === "sample" && (
            <RedirectPanel
              testid="contact-sample-redirect"
              heading="Full sample request flow"
              body="For the complete sample request, please use our dedicated form at /request-sample. It captures the machine, substrate and application detail our team needs to prepare a sample."
              ctaLabel="Go to Sample Request"
              ctaTo={`/request-sample${prefilledProduct ? `?product=${prefilledProduct}` : ""}`}
            />
          )}
          {tab === "distributor" && (
            <RedirectPanel
              testid="contact-distributor-redirect"
              heading="Distributor application"
              body="For the full distributor application, please use our dedicated form at /distributors. It captures the technical sales, market and coverage details required to evaluate a partnership."
              ctaLabel="Go to Distributor Application"
              ctaTo="/distributors"
            />
          )}
        </div>
      </section>
    </>
  );
};

export default Contact;
